/**
 * LandingContentSchema — describes which strings the CMS can override,
 * grouped into tabs shown in /landing-settings. Each entry maps a dotted
 * i18n key to a human label + baseline English copy shown as placeholder.
 *
 * Only strings listed here appear in the admin editor. Anything else in
 * the bundled locales stays untouched.
 */

const F = (path, label, hint, defaultEn, opts = {}) => ({
  path,
  label,
  hint,
  defaultEn,
  type: opts.type || "text",
  ...opts,
});

export const LANDING_SECTIONS = [
  {
    id: "global",
    title: "Global Branding",
    description:
      "System name, brand name, browser title and default language. These affect every page.",
    branding: true,
  },
  {
    id: "visuals",
    title: "Logo & Visuals",
    description:
      "Main brand logo used in the sidebar, receipts, PDFs and the login page. Upload · replace · delete.",
    visuals: true,
  },
  {
    id: "home",
    title: "Home / Hero",
    description:
      "First screen visitors see on the public landing page. Supports the {{brand}} placeholder to insert your company name.",
    images: [
      {
        slot: "hero_bg",
        label: "Hero background image",
        description: "Full-width photo shown behind the couple illustration on the landing hero. Leave empty to keep the current bundled default.",
        recommended: { width: 1920, height: 1080, ratio: "16:9" },
      },
    ],
    fields: [
      F("landing.hero_kicker", "Kicker / small label", null, "Instant · Secure · Global"),
      F("landing.hero_title", "Main title", "Use {{brand}} to insert the company name.", "Connect the world with {{brand}}"),
      F(
        "landing.hero_subtitle",
        "Subtitle / description",
        null,
        "A simple, fast and secure platform for international mobile topups, digital payments and reseller network management.",
        { type: "textarea" },
      ),
      F("landing.cta_login", "Primary button label (logged out)", null, "Login"),
      F("landing.cta_dashboard", "Primary button label (logged in)", null, "Go to Dashboard"),
      F("landing.hero_cta_learn", "Secondary button label", null, "Explore services"),
      F("landing.hero_trust_secure", "Trust badge 1", null, "Bank-grade encryption"),
      F("landing.hero_trust_instant", "Trust badge 2", null, "Real-time settlement"),
      F("landing.hero_trust_multi", "Trust badge 3", null, "Multi-role network"),
      // Feb 2026 · Promo Bar / Sliding Info — scrolling marquee shown at
      // the top of the hero section on the public landing page. Leave
      // empty (or turn off the toggle in the sidebar) to hide the bar
      // completely — no empty space is reserved.
      F(
        "landing.promo_text",
        "Promo Bar / Sliding Info · text",
        "Shown as a slowly scrolling ticker at the top of the hero section. Toggle it on/off with the 'Show promo bar' checkbox below. Leave empty to hide.",
        "",
        { type: "textarea" },
      ),
    ],
    // Non-translation toggles rendered under this section's fields. They
    // live inside the top-level `toggles` block of the CMS document.
    toggles: [
      {
        key: "promo_ticker",
        label: "Show promo bar on the landing page",
        description: "When on and the promo text is not empty, a slowly scrolling info bar appears above the hero.",
      },
    ],
  },
  {
    id: "navigation",
    title: "Header / Navigation",
    description: "Top navigation labels shown in the landing header.",
    fields: [
      F("landing.nav_home", "Home", null, "Home"),
      F("landing.nav_about", "About", null, "About"),
      F("landing.nav_services", "Services", null, "Services"),
      F("landing.nav_products", "Products", null, "Products"),
      F("landing.nav_partners", "Partners", null, "Partners"),
      F("landing.nav_contact", "Contact", null, "Contact"),
    ],
  },
  {
    id: "about",
    title: "About",
    description: "About section content and the value-proposition cards.",
    images: [
      {
        slot: "about_bg",
        label: "About section background",
        description: "Optional decorative background for the About section.",
        recommended: { width: 1920, height: 900, ratio: "16:7.5" },
      },
    ],
    fields: [
      F("landing.about_kicker", "Section kicker", null, "About us"),
      F("landing.about_title", "Section title", null, "Designed for resellers, chosen by agents."),
      F("landing.about_p1", "First paragraph", null, "{{brand}} is a global recharge platform designed for master agents and field agents. We give you the operational backbone — wallet, ledger, receipts and compliance — so you can focus on serving your customers.", { type: "textarea" }),
      F("landing.about_p2", "Second paragraph", null, "From single-country operations to multi-region reseller networks, our tools scale with you: transparent commissions, real-time analytics, and enterprise-grade auditability out of the box.", { type: "textarea" }),
      F("landing.value_secure_title", "Card 1 title", null, "Secure by design"),
      F("landing.value_secure_desc", "Card 1 description", null, "2FA, trusted-device flows, audit log on every action."),
      F("landing.value_realtime_title", "Card 2 title", null, "Real-time"),
      F("landing.value_realtime_desc", "Card 2 description", null, "Wallet, ledger and receipts settle instantly."),
      F("landing.value_hierarchy_title", "Card 3 title", null, "Multi-role"),
      F("landing.value_hierarchy_desc", "Card 3 description", null, "Master Agent · Agent · Collaborator."),
      F("landing.value_global_title", "Card 4 title", null, "Global"),
      F("landing.value_global_desc", "Card 4 description", null, "Multi-currency, multi-country, USD-anchored wallets."),
      F("landing.value_whitelabel_title", "Card 5 title", null, "White Label"),
      F("landing.value_whitelabel_desc", "Card 5 description", null, "Your brand, custom design, turnkey experience for your business."),
    ],
  },
  {
    id: "services",
    title: "Services",
    description: "Four service cards shown on the landing page.",
    fields: [
      F("landing.services_kicker", "Section kicker", null, "Services"),
      F("landing.services_title", "Section title", null, "Everything your reseller network needs."),
      F("landing.services_sub", "Section subtitle", null, "Four core services — one integrated platform, one wallet, one dashboard.", { type: "textarea" }),
      F("landing.svc_giftcards_title", "Card 1 title", null, "Gift Cards"),
      F("landing.svc_giftcards_desc", "Card 1 description", null, "Digital gift cards for popular brands and platforms, delivered instantly.", { type: "textarea" }),
      F("landing.svc_topup_title", "Card 2 title", null, "Topup & Bill Payments"),
      F("landing.svc_topup_desc", "Card 2 description", null, "Instant mobile recharges and utility bill payments to hundreds of operators worldwide.", { type: "textarea" }),
      F("landing.svc_intl_calls_title", "Card 3 title", null, "International Calls"),
      F("landing.svc_intl_calls_desc", "Card 3 description", null, "We know times are tough right now — but cost should never keep you from staying in touch with your loved ones.", { type: "textarea" }),
      F("landing.svc_api_title", "Card 4 title", null, "API Integration"),
      F("landing.svc_api_desc", "Card 4 description", null, "REST API for businesses and resellers to natively integrate topups, cash-outs and gift cards.", { type: "textarea" }),
    ],
  },
  {
    id: "products",
    title: "Products",
    description: "Web Portal and Mobile Application product cards.",
    images: [
      {
        slot: "products_web_master",
        label: "Master Agent dashboard screenshot",
        description: "Preview of the Master Agent portal shown inside the Web Portal card.",
        recommended: { width: 1600, height: 1000, ratio: "16:10" },
      },
      {
        slot: "products_web_agent",
        label: "Agent dashboard screenshot",
        description: "Preview of the Agent portal shown next to Master Agent.",
        recommended: { width: 1600, height: 1000, ratio: "16:10" },
      },
      {
        slot: "products_mobile",
        label: "Mobile app mockup",
        description: "Mobile app illustration shown in the Products section.",
        recommended: { width: 1080, height: 1920, ratio: "9:16" },
      },
    ],
    fields: [
      F("landing.products_kicker", "Section kicker", null, "Products"),
      F("landing.products_title", "Section title", null, "Two ways to run your business."),
      F("landing.prod_web_title", "Web Portal title", null, "Web Portal"),
      F("landing.prod_web_desc", "Web Portal description", null, "Full-featured reseller dashboard: real-time wallet, instant topups, transparent commissions, ID-verified customers, receipts & reports.", { type: "textarea" }),
      F("landing.prod_web_bullet_0", "Web bullet 1", null, "Multi-role hierarchy — Master Agent · Agent · Collaborator"),
      F("landing.prod_web_bullet_1", "Web bullet 2", null, "USD-only wallets with instant wallet funding"),
      F("landing.prod_web_bullet_2", "Web bullet 3", null, "PDF receipts and audit-grade reports"),
      F("landing.prod_mobile_title", "Mobile app title", null, "Mobile Application"),
      F("landing.prod_mobile_desc", "Mobile app description", null, "Send topups, verify identities, approve cash-outs and monitor your agents from anywhere.", { type: "textarea" }),
      F("landing.prod_mobile_bullet_0", "Mobile bullet 1", null, "Native experience for agents on the go"),
      F("landing.prod_mobile_bullet_1", "Mobile bullet 2", null, "Push notifications for pending approvals"),
      F("landing.prod_mobile_bullet_2", "Mobile bullet 3", null, "Offline-safe receipts"),
    ],
  },
  {
    id: "partners",
    title: "Partners / Operators",
    description: "Partners section header and disclaimer.",
    partners: true,
    fields: [
      F("landing.partners_kicker", "Section kicker", null, "Services available"),
      F("landing.partners_title", "Section title", null, "Send topups to leading operators."),
      F("landing.partners_sub", "Introduction", null, "Below is a sample of the operators available through our platform. Ask our support team for the full list — hundreds of networks across dozens of countries.", { type: "textarea" }),
      F("landing.partners_disclaimer", "Trademark disclaimer", null, "All brand names, trademarks and logos mentioned are the property of their respective owners and are shown solely to identify the services available through our platform.", { type: "textarea" }),
    ],
  },
  {
    id: "contact",
    title: "Contact / CTA",
    description: "Bottom call-to-action strip.",
    fields: [
      F("landing.cta_title", "CTA title", null, "Ready to start reselling?"),
      F("landing.cta_sub", "CTA subtitle", null, "Sign in to your account or contact our team to become a partner.", { type: "textarea" }),
      F("landing.cta_whatsapp", "WhatsApp button label", null, "Chat on WhatsApp"),
      F("landing.cta_email_label", "Email button label", "Text shown on the Send Email button. Leave blank to fall back to the English default.", "Send Email"),
    ],
    toggles: [
      {
        key: "email_button",
        label: "Show the Send Email button in the CTA",
        description: "When on, a third button 'Send Email' appears next to Login and WhatsApp. Clicking it opens the public contact form.",
      },
    ],
  },
  {
    id: "footer",
    title: "Footer",
    description: "Footer column titles and copyright strip.",
    fields: [
      F("landing.footer_tagline", "Company tagline (footer)", null, "A simple, fast and secure platform for global topups and digital payments.", { type: "textarea" }),
      F("landing.footer_company", "Column: Company", null, "Company"),
      F("landing.footer_services", "Column: Services", null, "Services"),
      F("landing.footer_products", "Column: Products", null, "Products"),
      F("landing.footer_legal", "Column: Legal", null, "Legal"),
      F("landing.footer_privacy", "Privacy Policy link", null, "Privacy Policy"),
      F("landing.footer_terms", "Terms & Conditions link", null, "Terms & Conditions"),
      F("landing.footer_rights", "Rights line", null, "All rights reserved."),
      F("landing.footer_login", "Login link", null, "Login"),
      F("landing.footer_forgot", "Forgot password link", null, "Forgot password"),
      F("landing.footer_follow", "Follow us label", null, "Follow us"),
      F("landing.footer_download", "Download app label", null, "Download the app"),
      F("landing.footer_play_get", "Play Store 'Get it on'", null, "Get it on"),
    ],
  },
  {
    id: "login",
    title: "Login Page",
    description:
      "All copy shown on the /login page. Form validation and 2FA logic are NOT editable here — only visible copy.",
    images: [
      {
        slot: "login_office",
        label: "Login office image (left panel)",
        description: "The image shown on the left of the login card. Keep faces and logo away from the edges — mobile crops from the sides.",
        recommended: { width: 1200, height: 1500, ratio: "4:5" },
      },
    ],
    fields: [
      F("auth.welcome_back", "Welcome title", null, "Welcome Back!"),
      F("auth.sign_in_title", "Subtitle above form", null, "Sign in to your account"),
      F("auth.sign_in_sub", "Introduction under the subtitle", null, "Enter your credentials to continue."),
      F("auth.username", "Username field label", null, "Username"),
      F("auth.username_placeholder", "Username placeholder", null, "Enter your username"),
      F("auth.password", "Password field label", null, "Password"),
      F("auth.password_placeholder", "Password placeholder", null, "Enter your password"),
      F("auth.sign_in", "Sign in button", null, "Sign in"),
      F("auth.signing_in", "Sign in button (loading)", null, "Signing in…"),
      F("auth.forgot_password", "Forgot password link", null, "Forgot password?"),
    ],
  },
  {
    id: "legal",
    title: "Legal (Privacy · Terms)",
    description:
      "Full text of the Privacy Policy and Terms & Conditions pages, editable in each language. Content is served on /privacy and /terms.",
    legal: true,
  },
  {
    id: "landing_stats",
    title: "Landing Page Statistics",
    description:
      "The 'We speak with numbers' block on the public homepage — Operators / Countries / Users / Customer Support. Toggle the whole section on/off and edit each stat's label and value.",
    landing_stats: true,
  },
  {
    id: "design",
    title: "Design & Layout",
    description:
      "Brand colours, hero overlay opacity, primary button colour and drag-drop section ordering.",
    design: true,
  },
  {
    id: "seo",
    title: "SEO",
    description:
      "Page title, meta description, keywords, Open Graph and search engine indexation. Configure separately for each supported language.",
    seo: true,
  },
  {
    id: "social",
    title: "Social & App Stores",
    description:
      "Facebook · Instagram · X · YouTube · TikTok links, plus Google Play / App Store badges.",
    social: true,
  },
  {
    id: "history",
    title: "Version history",
    description:
      "Every publish saves a snapshot. Browse and restore any previous version — a rollback re-publishes it immediately.",
    history: true,
  },
];
export const CMS_LANGUAGES = [
  { code: "en", label: "English",  flag: "US" },
  { code: "es", label: "Español",  flag: "ES" },
  { code: "fr", label: "Français", flag: "FR" },
  { code: "ht", label: "Kreyòl",   flag: "HT" },
];
